import React, { useState } from 'react';
import { Box, Button, MenuItem, Typography } from '@material-ui/core';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Modal from './Modal';
import { CustomTextField } from '../Textfield';
import {
    changeSlotCapacity,
    getCapacityApprovers,
} from '../../store/actions/settingsActionCreator';
import { resolveApproverNames, RoutedToLine } from '../../utils/capacityApproval';

// Slot Capacity Approval — single-slot Edit Capacity. No live "allowed range" preview here: the
// backend doesn't yet return per-row released capacity/threshold data (a confirmed, deferred
// backend gap — see artifacts/slot-capacity-approval-fe-plan.md), so this modal can only submit
// and react to whatever the backend decides (AUTO_APPLIED vs PENDING), not predict it.
const REASONS = [
    'Rider no-show cover',
    'Volume spike / surge',
    'Client ask',
    'Forecast correction',
    'Planning error',
];

function EditCapacityModal({
    open,
    data = {},
    onClose,
    dispatchChangeSlotCapacity,
    dispatchGetCapacityApprovers,
}) {
    const [isLoading, toggleLoading] = useState(false);
    const [result, setResult] = useState(null); // { status, appliedCapacity, routedToTier, approverNames }
    const shiftId = data?.id ?? data?.shiftId;
    const currentCapacity = data?.capacity ?? data?.slotSize;
    const staffCount = data?.staffCount;

    const handleClose = () => {
        !!onClose && onClose();
    };

    const validationSchema = yup.object({
        newCapacity: yup
            .number('Capacity must be a number')
            .typeError('Capacity must be a number')
            .integer('Capacity must be a whole number')
            .min(
                staffCount ?? 0,
                staffCount
                    ? `Cannot be below the assigned staff count (${staffCount})`
                    : 'Capacity cannot be negative',
            )
            .required('New capacity is required'),
        reason: yup.string().required('Reason is required'),
    });

    const formik = useFormik({
        initialValues: {
            newCapacity: currentCapacity ?? '',
            reason: REASONS[0],
        },
        validationSchema,
        onSubmit: (values) => {
            toggleLoading(true);
            dispatchChangeSlotCapacity(
                shiftId,
                {
                    newCapacity: Number(values.newCapacity),
                    reason: values.reason,
                },
                (resp) => {
                    const body = resp?.response ?? {};
                    if (body?.status === 'PENDING' && body?.routedToTier) {
                        dispatchGetCapacityApprovers(
                            (aResp) => {
                                toggleLoading(false);
                                const approverNames = resolveApproverNames(body.routedToTier, {
                                    approvers: aResp?.response?.approvers,
                                    roleChain: aResp?.response?.roleChain,
                                });
                                setResult({ ...body, approverNames });
                            },
                            () => {
                                toggleLoading(false);
                                setResult(body);
                            },
                        );
                    } else {
                        toggleLoading(false);
                        setResult(body?.status ? body : { ...body, status: 'AUTO_APPLIED' });
                    }
                },
                () => {
                    toggleLoading(false);
                },
            );
        },
    });

    if (result) {
        const isPending = result.status === 'PENDING';
        return (
            <Modal
                onClose={handleClose}
                open={open}
                title={isPending ? 'Sent for approval' : 'Capacity updated'}
                subtitle={data?.slotShortName ?? data?.name}
                actions={[
                    <Button
                        key={1}
                        variant="contained"
                        color="primary"
                        className="saveButton"
                        disableElevation
                        onClick={handleClose}
                    >
                        Done
                    </Button>,
                ]}
                maxWidth="xs"
            >
                {isPending ? (
                    <>
                        <Typography variant="body2" style={{ marginBottom: 8 }}>
                            Capacity is unchanged until this is approved.
                        </Typography>
                        <Typography variant="body2">
                            <RoutedToLine
                                routedToTier={result.routedToTier}
                                approverNames={result.approverNames}
                                additionalApproverTier={result.additionalApproverTier}
                                additionalApproverNames={result.additionalApproverNames}
                                approverDeciderApplicable={result.approverDeciderApplicable}
                            />
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body2">
                        Capacity updated to {result?.appliedCapacity ?? formik.values.newCapacity}.
                        This applied immediately.
                    </Typography>
                )}
            </Modal>
        );
    }

    return (
        <Modal
            onClose={handleClose}
            open={open}
            title="Edit Capacity"
            subtitle={data?.slotShortName ?? data?.name}
            actions={[
                <Button
                    key={1}
                    variant="contained"
                    color="default"
                    className="cancelButton"
                    onClick={handleClose}
                    disableElevation
                >
                    Cancel
                </Button>,
                <Button
                    key={2}
                    variant="contained"
                    color="primary"
                    className="saveButton"
                    type="submit"
                    disableElevation
                >
                    Update Capacity
                </Button>,
            ]}
            loading={isLoading}
            isForm={true}
            onSubmit={formik.handleSubmit}
            maxWidth="xs"
        >
            <Box
                style={{
                    background: '#fafbfd',
                    border: '1px solid #ebedf8',
                    borderRadius: 8,
                    padding: '12px 14px',
                    marginBottom: 16,
                }}
            >
                <Typography variant="body2" color="textSecondary">
                    Current capacity <b style={{ color: '#14112d' }}>{currentCapacity ?? '-'}</b>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Assigned staff <b style={{ color: '#14112d' }}>{staffCount ?? '-'}</b>
                </Typography>
            </Box>
            <CustomTextField
                fullWidth={true}
                type="number"
                label="New Capacity"
                id="newCapacity"
                name="newCapacity"
                value={formik.values.newCapacity}
                onChange={formik.handleChange}
                error={
                    formik.touched.newCapacity
                        ? Boolean(formik.errors.newCapacity)
                        : false
                }
                helperText={
                    formik.touched.newCapacity && formik.errors.newCapacity
                }
                style={{ marginBottom: '16px' }}
                isRequired={true}
            />
            <CustomTextField
                fullWidth={true}
                select
                label="Reason"
                id="reason"
                name="reason"
                value={formik.values.reason}
                onChange={formik.handleChange}
                error={
                    formik.touched.reason ? Boolean(formik.errors.reason) : false
                }
                helperText={formik.touched.reason && formik.errors.reason}
                style={{ marginBottom: '4px' }}
                isRequired={true}
            >
                {REASONS.map((r) => (
                    <MenuItem key={r} value={r}>
                        {r}
                    </MenuItem>
                ))}
            </CustomTextField>
        </Modal>
    );
}

const mapDispatchToProps = (dispatch) => ({
    dispatchChangeSlotCapacity: (shiftId, data, onSuccess, onError) =>
        dispatch(changeSlotCapacity(shiftId, data, onSuccess, onError)),
    dispatchGetCapacityApprovers: (onSuccess, onError) =>
        dispatch(getCapacityApprovers(onSuccess, onError)),
});

const EditCapacityModalConnected = connect(
    null,
    mapDispatchToProps,
)(EditCapacityModal);

// Plain untyped wrapper — same pattern as UploadPayEarnModal.jsx. Needed because this modal is
// rendered from a .tsx caller (slot-listing/index.tsx); TS's strict inference over a connect()ed
// component's public prop type otherwise fails ("cannot be used as a JSX component").
function EditCapacityModalWrapper(props) {
    return <EditCapacityModalConnected {...props} />;
}
export default EditCapacityModalWrapper;
